import {
  ChevronDown,
  CircleParking,
  FileText,
  Gamepad2,
  Gauge,
  LineChart,
  LogOut,
  Menu,
  Settings,
  StopCircle,
  Users,
  X
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../context/AuthContext";
import { getUserBalance } from "../redux/entities/userEntity";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { Role } from "../utils/rolesUtil";

interface NavbarProps {
  window?: () => Window;
}

/* ================= Styled Components ================= */

const HeaderShell = styled.header`
  position: sticky;
  top: 0;
  z-index: 1030;
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
`;

const NavBarSurface = styled.nav`
  background: rgba(15, 23, 42, 0.94);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.4);
  padding: 0.6rem 1.25rem;
  transition: all 0.2s ease;
`;

const NavContainer = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const BrandSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BrandLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: #ffffff;
  font-weight: 800;
  letter-spacing: -0.4px;
  font-size: 1.35rem;
  line-height: 1;

  &:hover {
    color: #ffffff;
  }
`;

const BrandBadge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(59, 130, 246, 0.4);
  color: #ffffff;
  font-weight: 800;
  font-size: 1rem;
`;

const BrandText = styled.span`
  background: linear-gradient(135deg, #ffffff 0%, #cbd5e1 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

/* Desktop Navigation */
const DesktopNav = styled.div`
  display: none;
  align-items: center;
  gap: 4px;
  margin-left: 1.5rem;
  margin-right: auto;

  @media (min-width: 992px) {
    display: flex;
  }
`;

const NavItemWrapper = styled.div`
  position: relative;
`;

interface NavLinkStyledProps {
  $active?: boolean;
}

const NavLinkStyled = styled(Link) <NavLinkStyledProps>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 12px;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.15s ease;
  color: ${({ $active }) => ($active ? "#60a5fa" : "rgba(241, 245, 249, 0.8)")};
  background: ${({ $active }) => ($active ? "rgba(59, 130, 246, 0.12)" : "transparent")};

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const DropdownToggleBtn = styled.button<NavLinkStyledProps>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 12px;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 500;
  border: none;
  background: ${({ $active }) => ($active ? "rgba(59, 130, 246, 0.12)" : "transparent")};
  color: ${({ $active }) => ($active ? "#60a5fa" : "rgba(241, 245, 249, 0.8)")};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.08);
  }
`;

interface DropdownMenuWrapperProps {
  $isOpen: boolean;
}

const DropdownMenuWrapper = styled.div<DropdownMenuWrapperProps>`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 210px;
  background: rgba(23, 31, 48, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 6px;
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255, 255, 255, 0.05);
  display: ${({ $isOpen }) => ($isOpen ? "flex" : "none")};
  flex-direction: column;
  gap: 2px;
  z-index: 1050;
  animation: fadeIn 0.18s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

interface DropdownItemStyledProps {
  $active?: boolean;
}

const DropdownItemStyled = styled(Link) <DropdownItemStyledProps>`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 12px;
  border-radius: 7px;
  font-size: 0.85rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.15s ease;
  color: ${({ $active }) => ($active ? "#60a5fa" : "#cbd5e1")};
  background: ${({ $active }) => ($active ? "rgba(59, 130, 246, 0.15)" : "transparent")};

  &:hover {
    color: #ffffff;
    background: rgba(59, 130, 246, 0.12);
    transform: translateX(2px);
  }
`;

/* Points Pill Component */
const PointsPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(5, 150, 105, 0.18) 100%);
  border: 1px solid rgba(52, 211, 153, 0.35);
  border-radius: 9999px;
  padding: 4px 12px 4px 6px;
  box-shadow: 0 2px 10px rgba(16, 185, 129, 0.15);
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(52, 211, 153, 0.55);
    box-shadow: 0 2px 14px rgba(16, 185, 129, 0.25);
  }
`;

const PointsIconBadge = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  box-shadow: 0 0 10px rgba(16, 185, 129, 0.45);
  flex-shrink: 0;
`;

const PointsLabel = styled.span`
  font-size: 0.76rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  letter-spacing: 0.3px;
`;

const PointsAmount = styled.span`
  font-size: 0.94rem;
  font-weight: 700;
  color: #f8fafc;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.2px;
`;

/* Desktop User Section */
const DesktopUserSection = styled.div`
  display: none;
  align-items: center;
  gap: 12px;

  @media (min-width: 992px) {
    display: flex;
  }
`;

const UserProfileChip = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 9999px;
  padding: 4px 12px 4px 5px;
`;

const UserAvatar = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.78rem;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(99, 102, 241, 0.35);
  flex-shrink: 0;
`;

const UserMetaCol = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.15;
`;

const UserName = styled.span`
  font-size: 0.82rem;
  font-weight: 600;
  color: #f1f5f9;
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserPhone = styled.span`
  font-size: 0.72rem;
  color: #94a3b8;
`;

const LogoutIconButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  width: 36px;
  height: 36px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(239, 68, 68, 0.15);
    border-color: rgba(239, 68, 68, 0.35);
    color: #f87171;
    transform: translateY(-1px);
  }
`;

/* Mobile Section */
const MobileControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (min-width: 992px) {
    display: none;
  }
`;

const MenuToggleBtn = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.06);
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

interface MobileMenuDrawerProps {
  $isOpen: boolean;
}

const MobileMenuDrawer = styled.div<MobileMenuDrawerProps>`
  display: ${({ $isOpen }) => ($isOpen ? "flex" : "none")};
  flex-direction: column;
  gap: 6px;
  padding: 14px 4px 6px 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  margin-top: 10px;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (min-width: 992px) {
    display: none;
  }
`;

const MobileUserCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 10px 14px;
  margin-bottom: 6px;
`;

const MobileNavLink = styled(Link) <NavLinkStyledProps>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.92rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  color: ${({ $active }) => ($active ? "#60a5fa" : "rgba(241, 245, 249, 0.85)")};
  background: ${({ $active }) => ($active ? "rgba(59, 130, 246, 0.12)" : "transparent")};

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.06);
  }
`;

const MobileDropdownHeader = styled.button<NavLinkStyledProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.92rem;
  font-weight: 500;
  border: none;
  background: transparent;
  color: ${({ $active }) => ($active ? "#60a5fa" : "rgba(241, 245, 249, 0.85)")};
  cursor: pointer;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.06);
  }
`;

interface MobileSubMenuProps {
  $isOpen?: boolean;
}

const MobileSubMenu = styled.div<MobileSubMenuProps>`
  display: ${({ $isOpen }) => ($isOpen ? "flex" : "none")};
  flex-direction: column;
  gap: 2px;
  padding-left: 20px;
  margin: 4px 0;
  border-left: 2px solid rgba(59, 130, 246, 0.3);
`;

const MobileLogoutBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.92rem;
  font-weight: 500;
  border: none;
  background: rgba(239, 68, 68, 0.08);
  color: #f87171;
  cursor: pointer;
  margin-top: 8px;

  &:hover {
    background: rgba(239, 68, 68, 0.16);
  }
`;

/* ================= Menu Configuration ================= */

const menuData = [
  {
    label: "Dashboard",
    icon: "FaTachometerAlt",
    link: "/",
    subMenu: [],
    roles: [Role.SuperAdmin, Role.Admin, Role.Agent, Role.Customer]
  },
  {
    label: "Master",
    icon: "FaCog",
    link: "#",
    roles: [Role.SuperAdmin, Role.Admin, Role.Agent],
    subMenu: [
      {
        label: "Customers",
        icon: "FaUsers",
        link: "/users",
        roles: [Role.SuperAdmin, Role.Admin, Role.Agent]
      },
      {
        label: "Agents",
        icon: "FaUsers",
        link: "/agents",
        roles: [Role.SuperAdmin, Role.Admin]
      },
      {
        label: "Admins",
        icon: "FaUsers",
        link: "/admins",
        roles: [Role.SuperAdmin]
      },
      {
        label: "Managers",
        icon: "FaUsers",
        link: "/managers",
        roles: [Role.SuperAdmin]
      },
      {
        label: "Games",
        icon: "FaGamepad",
        link: "/games",
        roles: [Role.SuperAdmin]
      },
      {
        label: "Game Config",
        icon: "FaGamepad",
        link: "/games/config",
        roles: [Role.SuperAdmin]
      },
      {
        label: "Result Times",
        icon: "FaGamepad",
        link: "/resultTimes",
        roles: [Role.SuperAdmin]
      },
      {
        label: "Dynamic Limit",
        icon: "FaUsers",
        link: "/dynamicLimit",
        roles: [Role.SuperAdmin]
      },
      {
        label: "Manager Permissions",
        icon: "FaUsers",
        link: "/manager/permissions",
        roles: [Role.SuperAdmin]
      }
    ]
  },
  {
    label: "Client Reports",
    icon: "FaCog",
    link: "#",
    roles: [Role.SuperAdmin],
    subMenu: [
      {
        label: "Single Report",
        icon: "FaUsers",
        link: "/reports/singleReport",
        roles: [Role.SuperAdmin]
      },
      {
        label: "Patti Report",
        icon: "FaUsers",
        link: "/reports/pattiReport",
        roles: [Role.SuperAdmin]
      }
    ]
  },
  {
    label: "Reports",
    icon: "FaFileAlt",
    link: "#",
    roles: [Role.SuperAdmin, Role.Admin, Role.Agent, Role.Customer],
    subMenu: [
      {
        label: "Points Transaction History",
        icon: "FaUsers",
        link: "/reports/pointTrans",
        roles: [Role.SuperAdmin, Role.Admin, Role.Agent, Role.Customer]
      },
      {
        label: "Day to Day Trans",
        icon: "FaGamepad",
        link: "/reports/trans",
        roles: [Role.SuperAdmin, Role.Admin, Role.Agent, Role.Customer]
      },
      {
        label: "Bet Report",
        icon: "FaUsers",
        link: "/reports/betView",
        roles: [Role.SuperAdmin, Role.Admin, Role.Agent, Role.Customer]
      },
      // {
      //   label: "Scheduled Bets",
      //   icon: "FaUsers",
      //   link: "/reports/scheduled-bets",
      //   roles: [Role.SuperAdmin, Role.Customer]
      // },
      {
        label: "Game Report",
        icon: "FaUsers",
        link: "/reports/gameReport",
        roles: [Role.SuperAdmin]
      },
      {
        label: "Business",
        icon: "FaGamepad",
        link: "/reports/business",
        roles: [Role.SuperAdmin]
      }
    ]
  },
  {
    label: "Publish Result",
    icon: "FaChartLine",
    link: "/results",
    subMenu: [],
    roles: [Role.SuperAdmin, Role.Manager]
  },
  {
    label: "Change Password",
    icon: "FaCog",
    link: "/changePassword",
    subMenu: [],
    roles: [Role.SuperAdmin, Role.Admin, Role.Agent, Role.Customer, Role.Manager]
  }
];

/* ================= Navbar Component ================= */

const Navbar: React.FC<NavbarProps> = () => {
  const { user, isAuthenticated, role, logOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const userPoint = useAppSelector((state) => state.entities.userReducer.userPoint);

  const [desktopDropdown, setDesktopDropdown] = useState<string | null>(null);
  const [mobileDropdown, setMobileDropdown] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const desktopNavRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user !== null && isAuthenticated) {
      dispatch(getUserBalance());
    }
  }, [user, isAuthenticated, dispatch]);

  // Close desktop dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (desktopNavRef.current && !desktopNavRef.current.contains(event.target as Node)) {
        setDesktopDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close mobile menu and dropdowns on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setDesktopDropdown(null);
    setMobileDropdown(null);
  }, [location.pathname]);

  const getFilteredMenuData = () => {
    return menuData
      .filter((item) => item.roles && item.roles.includes(role))
      .map((item) => ({
        ...item,
        subMenu: item.subMenu ? item.subMenu.filter((sub) => sub?.roles?.includes(role)) : []
      }));
  };

  const getIcon = (iconName: string) => {
    const icons: Record<string, React.ElementType> = {
      FaTachometerAlt: Gauge,
      FaUsers: Users,
      FaGamepad: Gamepad2,
      FaStopCircle: StopCircle,
      FaChartLine: LineChart,
      FaCog: Settings,
      FaSignOutAlt: LogOut,
      FaFileAlt: FileText
    };
    return icons[iconName] || Gauge;
  };

  const handleLogout = () => {
    logOut();
    navigate("/");
  };

  const isPathActive = (path: string) => location.pathname === path;
  const isDropdownActive = (items: { link: string }[]) =>
    items.some((i) => location.pathname === i.link);

  const userInitial = user?.name ? user.name.charAt(0).toUpperCase() : "U";
  const formattedBalance = Number(userPoint || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  return (
    <HeaderShell id="header">
      <NavBarSurface>
        <NavContainer>
          {/* Brand */}
          <BrandSection>
            <BrandLink to="/">
              <BrandBadge>B</BrandBadge>
              <BrandText>Bizzboy</BrandText>
            </BrandLink>
          </BrandSection>

          {/* Desktop Nav Items */}
          <DesktopNav ref={desktopNavRef}>
            {getFilteredMenuData().map((item, index) => {
              const IconComp = getIcon(item.icon);
              const hasSub = item.subMenu.length > 0;
              const isItemActive = hasSub ? isDropdownActive(item.subMenu) : isPathActive(item.link);

              if (hasSub) {
                const isOpen = desktopDropdown === item.label;
                return (
                  <NavItemWrapper key={index}>
                    <DropdownToggleBtn
                      $active={isItemActive}
                      type="button"
                      onClick={() => setDesktopDropdown(isOpen ? null : item.label)}
                    >
                      <IconComp size={15} />
                      <span>{item.label}</span>
                      <ChevronDown
                        size={13}
                        style={{
                          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s ease"
                        }}
                      />
                    </DropdownToggleBtn>

                    <DropdownMenuWrapper $isOpen={isOpen}>
                      {item.subMenu.map((sub, subIdx) => {
                        const SubIcon = getIcon(sub.icon);
                        const isSubActive = isPathActive(sub.link);
                        return (
                          <DropdownItemStyled
                            key={subIdx}
                            to={sub.link}
                            $active={isSubActive}
                            onClick={() => setDesktopDropdown(null)}
                          >
                            <SubIcon size={14} style={{ opacity: 0.8 }} />
                            <span>{sub.label}</span>
                          </DropdownItemStyled>
                        );
                      })}
                    </DropdownMenuWrapper>
                  </NavItemWrapper>
                );
              }

              return (
                <NavLinkStyled key={index} to={item.link} $active={isItemActive}>
                  <IconComp size={15} />
                  <span>{item.label}</span>
                </NavLinkStyled>
              );
            })}
          </DesktopNav>

          {/* Desktop User Section */}
          <DesktopUserSection>
            <PointsPill title="Available Points">
              <PointsIconBadge>
                <CircleParking size={14} strokeWidth={2.4} />
              </PointsIconBadge>
              <PointsLabel>Balance</PointsLabel>
              <PointsAmount>{formattedBalance}</PointsAmount>
            </PointsPill>

            <UserProfileChip>
              <UserAvatar>{userInitial}</UserAvatar>
              <UserMetaCol>
                <UserName>{user?.name || "User"}</UserName>
                {user?.phone && <UserPhone>{user.phone}</UserPhone>}
              </UserMetaCol>
            </UserProfileChip>

            <LogoutIconButton title="Logout" onClick={handleLogout}>
              <LogOut size={16} />
            </LogoutIconButton>
          </DesktopUserSection>

          {/* Mobile Right Controls */}
          <MobileControls>
            <PointsPill title="Available Points">
              <PointsIconBadge>
                <CircleParking size={13} strokeWidth={2.4} />
              </PointsIconBadge>
              <PointsAmount>{formattedBalance}</PointsAmount>
            </PointsPill>

            <MenuToggleBtn
              type="button"
              aria-label="Toggle navigation menu"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </MenuToggleBtn>
          </MobileControls>
        </NavContainer>

        {/* Mobile Dropdown Drawer */}
        <MobileMenuDrawer $isOpen={mobileMenuOpen}>
          <MobileUserCard>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <UserAvatar>{userInitial}</UserAvatar>
              <UserMetaCol>
                <UserName>{user?.name || "User"}</UserName>
                {user?.phone && <UserPhone>{user.phone}</UserPhone>}
              </UserMetaCol>
            </div>
            <PointsPill>
              <PointsIconBadge>
                <CircleParking size={12} strokeWidth={2.4} />
              </PointsIconBadge>
              <PointsAmount>{formattedBalance}</PointsAmount>
            </PointsPill>
          </MobileUserCard>

          {getFilteredMenuData().map((item, index) => {
            const IconComp = getIcon(item.icon);
            const hasSub = item.subMenu.length > 0;
            const isItemActive = hasSub ? isDropdownActive(item.subMenu) : isPathActive(item.link);

            if (hasSub) {
              const isOpen = mobileDropdown === item.label;
              return (
                <div key={index}>
                  <MobileDropdownHeader
                    type="button"
                    $active={isItemActive}
                    onClick={() =>
                      setMobileDropdown(isOpen ? null : item.label)
                    }
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <IconComp size={16} />
                      <span>{item.label}</span>
                    </div>
                    <ChevronDown
                      size={15}
                      style={{
                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease"
                      }}
                    />
                  </MobileDropdownHeader>

                  <MobileSubMenu $isOpen={isOpen}>
                    {item.subMenu.map((sub, subIdx) => {
                      const SubIcon = getIcon(sub.icon);
                      const isSubActive = isPathActive(sub.link);
                      return (
                        <MobileNavLink
                          key={subIdx}
                          to={sub.link}
                          $active={isSubActive}
                          onClick={() => {
                            setMobileMenuOpen(false);
                            setMobileDropdown(null);
                          }}
                        >
                          <SubIcon size={14} style={{ opacity: 0.8 }} />
                          <span>{sub.label}</span>
                        </MobileNavLink>
                      );
                    })}
                  </MobileSubMenu>
                </div>
              );
            }

            return (
              <MobileNavLink
                key={index}
                to={item.link}
                $active={isItemActive}
                onClick={() => setMobileMenuOpen(false)}
              >
                <IconComp size={16} />
                <span>{item.label}</span>
              </MobileNavLink>
            );
          })}

          <MobileLogoutBtn onClick={handleLogout}>
            <LogOut size={16} />
            <span>Logout</span>
          </MobileLogoutBtn>
        </MobileMenuDrawer>
      </NavBarSurface>
    </HeaderShell>
  );
};

export default Navbar;
